import type { Result } from "neverthrow";
import { err, ok, Result as Results } from "neverthrow";
import type { ZodType } from "zod";
import z from "zod";

import {
  buildInvalidError,
  getErrorMessage,
  type InvalidError,
} from "@thalamos/common";

import type { PublishedInstrumentSchema } from "./publishedSchema.js";

/**
 * A validator for a published instrument, built from the schema as stored.
 *
 * Built from the row rather than from the module that authored it, which is
 * what makes a rolled-back build able to validate a submission against a
 * version it no longer carries any code for. It is also the same artefact a
 * third party validates against before sending, so there is one statement of
 * what a valid document is rather than two that can disagree.
 *
 * The type says a schema has the published shape, not that a validator can be
 * built from it — a `$ref` naming a definition the document does not carry
 * parses and then throws — so this answers rather than throwing.
 */
export const validatorFor = Results.fromThrowable(
  (schema: PublishedInstrumentSchema): ZodType => z.fromJSONSchema(schema),
  (cause) =>
    buildInvalidError({
      message: "No validator can be built from this instrument",
      issues: [getErrorMessage(cause)],
    }),
);

/**
 * Where a problem is, as a JSON pointer to its place in a document —
 * `/trauma/adjustment_to_trauma/response`.
 *
 * A pointer rather than a sentence naming the group and the item, because
 * documents are addressed that way and a pointer is what a caller can act on
 * without parsing prose.
 *
 * The whole document is the empty string, per RFC 6901. `/` would name the
 * member whose key is empty, which is a place no document has.
 */
export const pointerFor = (path: readonly PropertyKey[]): string =>
  path.length === 0
    ? ""
    : path
        .map(
          (segment) =>
            `/${String(segment).replaceAll("~", "~0").replaceAll("/", "~1")}`,
        )
        .join("");

/** One thing the instrument refuses in a document: where, and in its validator's words. */
export type InstrumentIssue = {
  readonly path: readonly PropertyKey[];
  readonly message: string;
};

/**
 * Everything the instrument refuses in a document, as data, for a caller that
 * reports problems in its own words rather than the validator's.
 */
export const instrumentIssuesIn = (
  schema: PublishedInstrumentSchema,
  document: unknown,
): Result<InstrumentIssue[], InvalidError> =>
  validatorFor(schema).map((validator) => {
    const parsed = validator.safeParse(document);

    return parsed.success
      ? []
      : parsed.error.issues.map((issue) => ({
          path: issue.path,
          message: issue.message,
        }));
  });

/**
 * Whether a document is a valid submission of this instrument.
 *
 * Every problem is reported, not the first: the reader is a person or a system
 * fixing a submission, and a round trip per mistake is what that turns into
 * otherwise.
 */
export const validateDocument = (
  schema: PublishedInstrumentSchema,
  document: unknown,
): Result<void, InvalidError> =>
  instrumentIssuesIn(schema, document).andThen((issues) =>
    issues.length === 0
      ? ok(undefined)
      : err(
          buildInvalidError({
            message: "The submission is not valid against its instrument",
            issues: issues.map(
              (issue) => `${pointerFor(issue.path)}: ${issue.message}`,
            ),
          }),
        ),
  );
